import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Paper, Typography, Button } from '@mui/material';
import { getCurrentUser } from '../../services/auth.ts';
import Login from './Login.tsx';
import SignUp from './SignUp.tsx';
import ForgotPassword from './ForgotPassword.tsx';

// AuthState type defines the possible authentication screens
type AuthState = 'loading' | 'login' | 'signup' | 'forgot-password';

interface AuthControllerProps {
  onAuthenticated: () => void;
}

const AuthController: React.FC<AuthControllerProps> = ({ onAuthenticated }) => {
  const [authState, setAuthState] = useState<AuthState>('loading');

  // Check if user is already authenticated on component mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const user = await getCurrentUser();
        if (user) {
          onAuthenticated();
        } else {
          setAuthState('login');
        }
      } catch (error) {
        console.error('Error checking authentication status:', error);
        setAuthState('login');
      }
    };

    checkAuthStatus();
  }, [onAuthenticated]);

  // Navigate to login screen
  const navigateToLogin = () => {
    setAuthState('login');
  };

  // Navigate to signup screen
  const navigateToSignUp = () => {
    setAuthState('signup');
  };

  // Navigate to forgot password screen
  const navigateToForgotPassword = () => {
    setAuthState('forgot-password');
  };

  // Handle successful login
  const handleLogin = () => {
    onAuthenticated();
  };

  // Handle successful signup (navigates to login)
  const handleSignUpComplete = () => {
    setAuthState('login');
  };

  // Handle successful password reset (navigates to login)
  const handlePasswordResetComplete = () => {
    setAuthState('login');
  };

  // Add handler for dev login button
  const handleDevLogin = () => {
    // This is only for development environment
    if (process.env.NODE_ENV === 'development') {
      console.log('Development mode: Quick login');
      onAuthenticated();
    }
  };

  // Render appropriate component based on authState
  const renderAuthComponent = () => {
    switch (authState) {
      case 'loading':
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '100%',
              padding: 4
            }}
          >
            <CircularProgress sx={{ color: '#8A2BE2' }} />
          </Box>
        );
      case 'login':
        return (
          <Login
            onLogin={handleLogin}
            onNavigateToSignUp={navigateToSignUp}
            onNavigateToForgotPassword={navigateToForgotPassword}
          />
        );
      case 'signup':
        return (
          <SignUp
            onNavigateToLogin={navigateToLogin}
            onSignUpComplete={handleSignUpComplete}
          />
        );
      case 'forgot-password':
        return (
          <ForgotPassword
            onNavigateToLogin={navigateToLogin}
            onPasswordResetComplete={handlePasswordResetComplete}
          />
        );
      default:
        return null;
    }
  };

  // If in dev mode, show the quick login option
  if (process.env.NODE_ENV === 'development') {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'rgba(26, 26, 46, 0.7)',
        backdropFilter: 'blur(10px)',
        padding: 4
      }}>
        <Paper sx={{ 
          padding: 4, 
          maxWidth: 500,
          width: '100%',
          background: 'rgba(26, 26, 46, 0.7)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: '1px solid rgba(138, 43, 226, 0.2)'
        }}>
          {authState === 'loading' ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress sx={{ color: '#8A2BE2' }} />
            </Box>
          ) : (
            <>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom color="primary">
                  Sign In
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Enter your credentials to access your account
                </Typography>
              </Box>
              
              {/* Normal authentication component */}
              {renderAuthComponent()}
              
              {/* Development quick login button */}
              <Box sx={{ mt: 3 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  onClick={handleDevLogin}
                  sx={{ 
                    mt: 2,
                    borderColor: 'rgba(138, 43, 226, 0.5)',
                    color: '#8A2BE2',
                    '&:hover': {
                      borderColor: '#8A2BE2',
                      backgroundColor: 'rgba(138, 43, 226, 0.1)'
                    }
                  }}
                >
                  Development Quick Login
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                  This button only appears in development mode
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    );
  }

  // In production, just use the original auth components with proper styling
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'rgba(26, 26, 46, 0.7)',
      backdropFilter: 'blur(10px)',
      padding: 4
    }}>
      {authState === 'loading' ? (
        <CircularProgress sx={{ color: '#8A2BE2' }} />
      ) : (
        <Paper sx={{
          padding: 4,
          maxWidth: 500,
          width: '100%',
          background: 'rgba(26, 26, 46, 0.7)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: '1px solid rgba(138, 43, 226, 0.2)'
        }}>
          {renderAuthComponent()}
        </Paper>
      )}
    </Box>
  );
};

export default AuthController; 